import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  OnboardingProgress,
  OnboardingSampleData,
  OnboardingStepKey,
  OnboardingStepStatus,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  dismissGuidedTour,
  getOnboarding,
  loadSampleData,
  removeSampleData,
  setOnboardingStep,
} from './onboarding-api.js';

/**
 * The checklist's data layer.
 *
 * Every mutation here answers with the WHOLE progress document rather than a fragment, and the
 * cache is replaced with it. That is deliberate: ticking one step can change several others —
 * loading sample data changes what the counts mean, and skipping the last outstanding step
 * finishes the setup — so a client-side patch of one row would leave the screen quietly
 * disagreeing with the server about how far along the shop is.
 *
 * Loading and removing sample data also invalidate the customer list, which they have just
 * rewritten. Nothing here is queueable offline: none of it is counter work, and a setup step
 * applied twenty minutes later, out of order, would be a strange thing to come back to.
 */

export const onboardingKeys = {
  all: ['onboarding'] as const,
  progress: () => ['onboarding', 'progress'] as const,
};

export function useOnboarding(
  options: { enabled?: boolean } = {},
): UseQueryResult<OnboardingProgress> {
  const client = useApiClient();
  return useQuery({
    queryKey: onboardingKeys.progress(),
    queryFn: () => getOnboarding(client),
    enabled: options.enabled ?? true,
  });
}

export interface SetStepInput {
  key: OnboardingStepKey;
  status: OnboardingStepStatus;
}

export function useSetOnboardingStep(): UseMutationResult<
  OnboardingProgress,
  unknown,
  SetStepInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, status }: SetStepInput) => setOnboardingStep(client, key, status),
    onSuccess: (progress) => {
      queryClient.setQueryData(onboardingKeys.progress(), progress);
    },
  });
}

export function useDismissGuidedTour(): UseMutationResult<OnboardingProgress, unknown, void> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => dismissGuidedTour(client),
    onSuccess: (progress) => {
      queryClient.setQueryData(onboardingKeys.progress(), progress);
    },
  });
}

export function useSampleData(): {
  load: UseMutationResult<OnboardingSampleData, unknown, void>;
  remove: UseMutationResult<OnboardingSampleData, unknown, void>;
} {
  const client = useApiClient();
  const queryClient = useQueryClient();

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    // The customer book is what just changed — three rows appeared in it, or left it.
    void queryClient.invalidateQueries({ queryKey: ['customers'] });
  };

  return {
    load: useMutation({ mutationFn: () => loadSampleData(client), onSuccess: refresh }),
    remove: useMutation({ mutationFn: () => removeSampleData(client), onSuccess: refresh }),
  };
}
